import React, { useState, useEffect } from 'react';
import { Snackbar, Alert, Badge } from '@mui/material';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import offlineApi from '../services/offlineApi';

/**
 * Component to show an indicator when the application is in offline mode
 */
const OfflineIndicator = () => {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [offlineMode, setOfflineMode] = useState(false);
  const [open, setOpen] = useState(false);
  const [messageShown, setMessageShown] = useState(false);

  // Check offline status
  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      setOpen(true);
    };

    const handleOffline = () => {
      setIsOffline(true);
      setOpen(true);
    };

    // Check if offline mode is enabled
    const checkOfflineMode = async () => {
      try {
        const enabled = await offlineApi.isOfflineModeEnabled();
        setOfflineMode(enabled);
        
        // Show message if we're offline or in forced offline mode
        if ((enabled || !navigator.onLine) && !messageShown) {
          setOpen(true);
          setMessageShown(true);
        }
      } catch (error) {
        console.error('Error checking offline mode:', error);
      }
    };

    // Initial check
    checkOfflineMode();

    // Event listeners for online/offline status
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Periodic check (every 30 seconds)
    const interval = setInterval(checkOfflineMode, 30000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(interval);
    };
  }, [messageShown]);

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  if (!isOffline && !offlineMode) {
    return null;
  }

  return (
    <>
      {/* Fixed position indicator in the corner */}
      <Badge
        sx={{
          position: 'fixed',
          bottom: '72px', // Positioned above the bottom navigation (56px + 16px margin)
          right: '16px',
          zIndex: 1100, // Higher z-index to appear above other elements
          backgroundColor: '#f44336',
          color: 'white',
          padding: '8px',
          borderRadius: '50%',
          boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CloudOffIcon />
      </Badge>

      {/* Notification that appears when status changes */}
      <Snackbar open={open} autoHideDuration={6000} onClose={handleClose}>
        <Alert 
          onClose={handleClose} 
          severity="info" 
          sx={{ width: '100%' }}
        >
          {isOffline ? 
            'You are offline. Using locally stored data.' : 
            offlineMode ? 
              'Offline mode is enabled. Some features may be limited.' : 
              'You are back online!'
          }
        </Alert>
      </Snackbar>
    </>
  );
};

export default OfflineIndicator;
